import { useEffect, useState, useCallback } from 'preact/hooks'
import { RANGE_INDICATOR } from 'libs/searchspring/components/collection/filters/FilterUtils'
import debounce from 'lodash.debounce'
import { formatValue } from 'libs/searchspring/components/collection/filters/FilterHooks'
import { globalState } from '@/components/helpers/utils'
import { formatMoney } from '@/components/helpers/currency-formatter'

export function FilterInputSlider({ filter, onFilter, fetchedFilter }) {
  const minimum = Math.min(filter.range[0], fetchedFilter?.range[0])
  const maximum = Math.max(filter.range[1], fetchedFilter?.range[1])
  const step = Math.min(filter.step ?? 1, 1)

  // Pass along the facet_active status to the RangeSlider
  const handleChange = (value) => {
    onFilter(`${filter.field}:${value.min}${RANGE_INDICATOR}${value.max}`)
  }

  return (
    <div class="flex w-full flex-col">
      <RangeSlider
        min={minimum}
        max={maximum}
        step={step}
        format={filter.format}
        onChange={handleChange}
        isActive={fetchedFilter?.facet_active > 0}
        field={filter.field}
      />
    </div>
  )
}

const RangeSlider = ({ min, max, step, onChange, format, isActive, field }) => {
  const [minValue, setMinValue] = useState(min)
  const [maxValue, setMaxValue] = useState(max)

  // Reset the slider positions when not active
  useEffect(() => {
    if (!isActive) {
      setMinValue(min)
      setMaxValue(max)
    }
  }, [isActive, min, max])

  // Create a debounced version of onChange that only fires after 300ms of inactivity
  const debouncedOnChange = useCallback(
    debounce((values) => {
      onChange(values)
    }, 300),
    [onChange]
  )

  const handleMinChange = (event) => {
    const newMinVal = Math.min(+event.target.value, maxValue - step)
    setMinValue(newMinVal)

    // Call the debounced function instead of onChange directly
    debouncedOnChange({ min: newMinVal, max: maxValue })
  }

  const handleMaxChange = (event) => {
    const newMaxVal = Math.max(+event.target.value, minValue + step)
    setMaxValue(newMaxVal)

    // Call the debounced function instead of onChange directly
    debouncedOnChange({ min: minValue, max: newMaxVal })
  }

  // Cancel the debounced function when component unmounts
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel()
    }
  }, [debouncedOnChange])

  const minPos = ((minValue - min) / (max - min)) * 100
  const maxPos = ((maxValue - min) / (max - min)) * 100
  const thumbStyle =
    'absolute top-1/2 w-[--thumb-size] h-[--thumb-size] bg-brand-black rounded-full -translate-x-1/2 -translate-y-1/2 z-2'
  const labelStyle = 'paragraph-xs'
  const inputStyle = 'range absolute size-full p-0 opacity-0 appearance-none pointer-events-none z-3'

  const formatLabelValue = (value) => {
    if (field === 'ss_price') {
      const format =
        value % 1 === 0
          ? globalState.shopify.moneyFormat.replace('amount', 'amount_no_decimals')
          : globalState.shopify.moneyFormat
      return formatMoney(value * 100, format)
    }
    return format ? formatValue(value, format) : value
  }

  return (
    <div className="flex w-full flex-col gap-y-1.5">
      <div className="flex items-center justify-between">
        <p className={labelStyle}>
          Min <span className="text-type-black-secondary">{formatLabelValue(min)}</span>
        </p>
        <p className={labelStyle}>
          Max <span className="text-type-black-secondary">{formatLabelValue(max)}</span>
        </p>
      </div>
      <div className="px-1.25 md:px-0.625">
        <div className="relative mx-[calc(var(--thumb-size)_/_-2)] flex h-[--thumb-size] items-center">
          <div className="absolute mx-[calc(var(--thumb-size)_/_-2)] h-[--thumb-size] w-[calc(100%_+_var(--thumb-size))]">
            <input
              className={inputStyle}
              type="range"
              value={minValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMinChange}
              aria-label="Min"
            />
            <input
              className={inputStyle}
              type="range"
              value={maxValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMaxChange}
              aria-label="Max"
            />
          </div>
          <div className="absolute h-[--thumb-size] w-full">
            <div className={thumbStyle} style={{ left: `${minPos}%` }} />
            <div className="absolute top-1/2 z-0 h-0.125 w-full -translate-y-1/2 bg-page-tertiary">
              <div
                className="absolute h-full bg-brand-black"
                style={{ left: `${minPos}%`, right: `${100 - maxPos}%` }}
              />
            </div>
            <div className={thumbStyle} style={{ left: `${maxPos}%` }} />
          </div>
        </div>
      </div>
    </div>
  )
}
